
import { mapActions } from 'vuex'
import { parseProductFromShopify } from '~/helpers/shopify/product'
import ProductTester from '~/helpers/shopify/tests'
import PdpLogs from '~/components/product-page/pdp-logs.vue'
import ProductPage from '~/components/product-page/product-page'
import productShopifyModel from '~/models/shopify/product'
import pageProductWordingModel from '~/models/storyblok/product-page'
import generateHeader from '~/helpers/header'
export default {
  name: 'ProductSlug',
  components: {
    PdpLogs,
    ProductPage,
  },
  // Unset Wishilisted Item and Close all notification modals before routing away
  beforeRouteLeave(to, from, next) {
    this.vxPDPResetProduct()
    next()
  },
  async asyncData({
    error,
    app: { $shopify, i18n, $storyblok, $sentryException },
    params: { slug },
    route: {
      query: { variant },
    },
    store: {
      state: {
        common: { model_groups },
      },
    },
    redirect,
  }) {
    try {
      // ----------------------------------------
      // MODEL PRODUCT PAGE
      // ----------------------------------------
      const pdpHelpers = require('~/helpers/shopify/pdp')

      const groupByHandle = model_groups?.groupByHandle || {}
      const handlesByGroup = model_groups?.handlesByGroup || {}
      const clearedSlug = slug.substring(0, slug.lastIndexOf('-'))

      // REDIRECT TO NEW SLUG IF OLD SLUG
      if (groupByHandle[clearedSlug]) {
        const objetTrouve = { [clearedSlug]: groupByHandle[clearedSlug] }
        const slugSplitted = slug.split('-')
        redirect(
          `${Object.values(objetTrouve)[0]}?variant=${
            slugSplitted[slugSplitted.length - 2]
          }-${slugSplitted[slugSplitted.length - 1]}`
        )
      }

      // CHECK MODELS && HANDLES FOUND
      if (!handlesByGroup[slug]) {
        throw new Error('model not found')
      }

      // PARAM INTERNAL TYPE & COLOR
      const variantSplit = variant ? variant.split('-') : null
      const paramInternalType = variant ? variantSplit[0] : null
      const paramColor = variant ? variantSplit[1] : null

      // GET PRODUCTS OF MODEL
      const handles = handlesByGroup[slug].map((elmt) => {
        const lastIndex = elmt.lastIndexOf('-')
        return lastIndex !== -1 ? elmt.substring(lastIndex + 1) : elmt
      })
      if (handles.length === 0) throw new Error('products not found in model')

      // CALL GET PRODUCTS BY INTERNAL TYPE TO SHOPIFY
      const {
        data: {
          products: { edges: productsData },
        },
      } = await $shopify.product.getProductsByInternalType(handles)
      if (!productsData) throw new Error('error call shopify')

      // GET MASTER PRODUCT & OTHERS COLORS
      const product = pdpHelpers.getMasterProduct(
        productsData,
        paramInternalType
      )
      const otherProducts = pdpHelpers.getOtherProducts(
        productsData,
        paramInternalType
      )
      if (!product) throw new Error('master product not found')

      // GET MASTER COLOR
      const color = pdpHelpers.getMasterColor(paramColor, product)

      // TEST MASTER PRODUCT
      const test = new ProductTester(product, color, i18n.locale)
      if (!test.productExist()) throw new Error('product not found')
      if (!test.colorExist()) throw new Error('color not found')
      if (!test.metafieldsExist()) throw new Error('metafields missing')
      if (!test.metafieldsLangExist())
        throw new Error('metafields lang missing')
      if (!test.metafieldsColorExist())
        throw new Error('metafields color missing')

      // PARSE MASTER PRODUCT
      const { result, logs } = parseProductFromShopify(
        product,
        i18n.locale,
        color
      )

      // BUILD COLORS FROM OTHER PRODUCTS
      result.colors = pdpHelpers.buildColorsFromOtherProducts(
        otherProducts,
        i18n.locale,
        result.colors
      )

      // STORYBLOK
      let pageProductWording
      let instagramWording
      let calloutsWording

      const { stories } = await $storyblok.getStories(
        `by_slugs=${i18n.locale}/pages/product-page/product-page,${i18n.locale}/pages/product-page/product-callouts`
      )

      stories.forEach((story) => {
        if (story.slug === 'product-page') pageProductWording = story.content
        if (story.slug === 'product-ig') instagramWording = story.content
        if (story.slug === 'product-callouts') calloutsWording = story.content
      })

      // RETURN
      return {
        productsData,
        product: {
          ...productShopifyModel,
          ...result,
        },
        wording: {
          productPage: {
            ...pageProductWordingModel,
            ...pageProductWording,
          },
          instagram: instagramWording,
          callouts: calloutsWording,
        },
        logs: {
          url: {
            slug,
            variant,
            paramInternalType,
            paramColor,
          },
          model: {
            model: slug,
            handles,
          },
          productLoaded: {
            handle: product.handle,
            color,
          },
          parsing: {
            logs,
          },
        },
      }
    } catch (e) {
      if (e.message === 'product not found' || e.message === 'model not found')
        error({
          statusCode: 404,
          message: `[PDP] product/retrieveProduct 404: ${e.message}`,
        })
      else {
        $sentryException(e, {
          source: 'product _slug.vue',
          level: 'fatal',
          extra: `[PDP] product/retrieveProduct error: ${e}`,
        })
        error({
          statusCode: 500,
          message: '[PDP] product/retrieveProduct 500: ' + e,
        })
      }
    }
  },
  data() {
    return {
      product: null,
      wording: null,
    }
  },
  head() {
    let metaData = {}

    if (this.product) {
      this.product.seo.title = this.product.seo.title
        ? this.product.seo.title
        : this.product.title
      if (!this.product.seo.description) {
        try {
          this.product.seo.description = new DOMParser()
            .parseFromString(this.product.description, 'text/html')
            .documentElement.textContent.substring(0, 160)
        } catch (e) {
          this.product.seo.description = ''
        }
      }

      this.product.seo.og_image = this.$config.DEFAULT_OG_IMAGE

      if (this.product.seo) {
        metaData = {
          ...generateHeader({
            ...this.product.seo,
            path: this.$route.path,
          }),
        }
      }

      // RICH SNIPPET
      let richSnippet = null
      try {
        // FIX: window is not defined
        const urlProduct =
          typeof window !== 'undefined' ? window.location.href : null
        const images = this.product?.medias?.[0]?.src
          ? [
              `${this.product.medias[0].src}&width=100`,
              `${this.product.medias[0].src}&width=400`,
              `${this.product.medias[0].src}&width=600`,
            ]
          : this.$config.DEFAULT_OG_IMAGE
        const price = this.product?.price
          ? parseFloat(this.product.price)
          : null
        let priceCurrency = null
        switch (this.product?.locale) {
          case 'en-US':
            priceCurrency = 'USD'
            break
          case 'en-CA':
            priceCurrency = 'CAD'
            break
          case 'en-EU':
            priceCurrency = 'EUR'
            break
          default:
            break
        }
        const sku =
          this.product?.internal_type && this.product?.internal_color_code
            ? `${this.product?.internal_type}-${this.product?.internal_color_code}`
            : null
        richSnippet = {
          '@context': 'https://schema.org/',
          '@type': 'Product',
          name: this.product?.title,
          image: images,
          description: this.product?.description
            ? this.product.description
                .replace(/<[^>]*>/g, '')
                .replace(/&nbsp;/g, ' ')
            : '',
          sku,
          brand: {
            '@type': 'Brand',
            name: 'Lole',
          },
          offers: {
            '@type': 'Offer',
            url: urlProduct,
            itemCondition: 'https://schema.org/NewCondition',
            price,
            priceCurrency,
            availability: this.product?.availability
              ? 'https://schema.org/InStock'
              : 'https://schema.org/SoldOut',
          },
        }
      } catch (e) {
        this.$sentryException(e, {
          source: 'product _slug.vue',
          level: 'warning',
          extra: `[PDP] richSnippet failed: ${e}`,
        })
      }

      const headScripts = {
        script: [
          {
            type: 'application/ld+json',
            json: richSnippet,
          },
          {
            src: '//cdn.searchspring.net/intellisuggest/is.min.js',
            body: true,
          },
        ],
      }

      return { ...metaData, ...headScripts }
    }
  },
  watch: {
    async $route() {
      await this.vxPDPResetProduct()
      await this.selectProductVariant()
      window.scrollTo({
        top: 0,
      })
    },
  },
  beforeMount() {
    // GTM
    try {
      this.$gtm.push({
        ecommerce: undefined,
        product: undefined,
        search: undefined,
      })
      this.$gtm.push({ page: { type: 'product page' } })
    } catch (e) {
      this.$sentryException(e, {
        source: 'product _slug.vue',
        level: 'fatal',
        extra: `[PAGE] GTM fail: ${e}`,
      })
    }

    // TRACKING (UA)
    try {
      this.$gtmEvents.DLProductPage(this.product)
      this.$gtmEvents.UAProductDetails(this.product, {
        page: 'product',
      })
    } catch (e) {
      this.$sentryException(e, {
        source: 'GTM page product',
        level: 'warning',
        extra: 'GTM page product failed',
      })
    }
  },
  mounted() {
    // IntelliSuggest
    if (this.product && window.IntelliSuggest && window.IntelliSuggest.init)
      this.intelliSuggestForSearchSpring()

    // FB TRACKING
    this.$nextTick(() => {
      setTimeout(() => {
        try {
          this.$fbEvents.ViewContent(this.product)
        } catch (e) {
          this.$sentryException(e, {
            source: 'FB page product',
            level: 'warning',
            extra: 'FB page product failed',
          })
        }
      }, 3000)
    })

    // Klaviyo
    try {
      const customer = this.$store.getters['customer/getCustomer']
      if (customer) this.$klaviyoEvents.ViewedProduct(this.product, customer)
    } catch (e) {
      this.$sentryException(e, {
        source: 'Klaviyo page product',
        level: 'warning',
        extra: 'Klaviyo page product failed',
      })
    }

    // GA4
    try {
      setTimeout(() => {
        this.$googleAnalyticsEvents.GA4ViewItem(this.product)
      }, 3000)
    } catch (e) {
      this.$sentryException(e, {
        source: 'GTM page product',
        level: 'warning',
        extra: 'GTM page product failed',
      })
    }
  },
  methods: {
    ...mapActions({
      vxCloseAllMobile: 'navigation/closeAllMobile',
      vxCloseAllDesktop: 'navigation/closeAllDesktop',
      vxPDPResetProduct: 'pdp/resetProduct',
    }),
    intelliSuggestForSearchSpring() {
      try {
        const customer = this.$store.getters['customer/getCustomer']
        const variant = this.product?.sizes.find((p) => {
          return p.availability
        })
        this.$searchspring.events.productPage(
          this.product,
          customer,
          variant?.sku
        )
      } catch (e) {
        this.$sentryException(e, {
          source: 'product page',
          level: 'warning',
          extra: 'intellisuggest error in PDP',
        })
      }
    },
    selectProductVariant() {
      const pdpHelpers = require('~/helpers/shopify/pdp')
      const variant = this.$route.query.variant
      const variantSplit = variant ? variant.split('-') : null
      const paramInternalType = variant ? variantSplit[0] : null
      const paramColor = variant ? variantSplit[1] : null

      const product = pdpHelpers.getMasterProduct(
        this.productsData,
        paramInternalType
      )

      const color = pdpHelpers.getMasterColor(paramColor, product)

      // PARSE MASTER PRODUCT
      const { result } = parseProductFromShopify(
        product,
        this.$i18n.locale,
        color
      )

      this.product = {
        ...productShopifyModel,
        ...result,
      }
    },
  },
}
